import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { t } from './i18n'
import { useFocusTrap } from './useFocusTrap'

/**
 * A real confirmation dialog.
 *
 * Leaving used to be a two-step pill: tap once and the label changed to "Leave the
 * class?", tap again to go. That is easy to miss — the button barely changes, it
 * un-confirms silently on blur, and a child who taps twice quickly is out of their
 * lesson with no idea why. Ending a paid lesson deserves an explicit yes.
 *
 * The safe action is listed FIRST and equally weighted, so the destructive one is
 * never the path of least resistance. Escape and a backdrop tap both cancel.
 */
export function ConfirmDialog({
  icon,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: {
  icon: string
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const firedRef = useRef(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  // aria-modal promises the page behind is inert; the trap makes it true.
  useFocusTrap(modalRef)
  // `onCancel` is typically an inline arrow, so it is a new function on every
  // parent render. Depending on it re-ran the effect and yanked focus back to
  // Stay each time — which would fight a user who had tabbed to the other button.
  // The ref keeps the Escape handler current without re-running anything.
  const cancelHandler = useRef(onCancel)
  cancelHandler.current = onCancel

  useEffect(() => {
    // Focus lands on the SAFE button, once on open: a stray Enter should keep you
    // in the lesson, not end it.
    cancelRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Capture phase + swallowed: the dialog is the innermost thing open, so
      // Escape answers it alone. Opened over the participants panel (remove a
      // student), a bubble listener let one Escape close the panel too.
      event.stopImmediatePropagation()
      cancelHandler.current()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [])

  // Rendered into <body>, NOT where it is written in the tree.
  //
  // The call site sits inside the control bar (next to the Leave button it
  // belongs to), and that bar has `backdrop-filter` — which makes it the
  // containing block for fixed-position descendants. So `position: fixed;
  // inset: 0` resolved against the BAR, giving the scrim a 72px-tall box: the
  // dim landed on the control bar instead of the lesson, and the dialog itself
  // hung 168px below the bottom of the viewport (measured). A portal detaches it
  // from every ancestor's filter/transform, so the overlay covers the viewport
  // no matter which component chooses to render it.
  return createPortal(
    <div
      className="modal-scrim"
      // A tap outside cancels, but only when it is the scrim itself — a drag that
      // ends outside the dialog should not throw someone out of their class.
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel()
      }}
    >
      <div ref={modalRef} className="modal" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <div className="modal__icon" aria-hidden="true">
          {icon}
        </div>
        <h2 className="modal__title" id="confirm-title">
          {title}
        </h2>
        <p className="modal__body">{body}</p>
        <div className="modal__actions">
          <button type="button" className="pill-button" onClick={onCancel} ref={cancelRef}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="pill-button pill-button--danger"
            // Once only: a double-tap on a laggy phone should not fire the
            // destructive action twice.
            onClick={() => {
              if (firedRef.current) return
              firedRef.current = true
              onConfirm()
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** Convenience wrapper for the only confirmation we currently have. */
export function LeaveDialog({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <ConfirmDialog
      icon="👋"
      title={t('room.leaveTitle')}
      body={t('room.leaveBody')}
      confirmLabel={t('room.leaveYes')}
      cancelLabel={t('room.leaveStay')}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}
